import React, { useCallback, useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import {
    Bold, Italic, Underline as UnderlineIcon, Strikethrough,
    AlignLeft, AlignCenter, AlignRight,
    List, ListOrdered, Heading1, Heading2, Heading3,
    LinkIcon, ImageIcon, Table as TableIcon,
    Undo, Redo, Palette, Highlighter, Minus,
    Braces, Link2, Eye, Pencil
} from 'lucide-react';

const PLACEHOLDERS = [
    { key: '{{teilnehmer_vorname}}', label: 'Vorname' },
    { key: '{{teilnehmer_nachname}}', label: 'Nachname' },
    { key: '{{teilnehmer_name}}', label: 'Voller Name' },
    { key: '{{teilnehmer_email}}', label: 'E-Mail' },
    { key: '{{seminar_titel}}', label: 'Seminar-Titel' },
    { key: '{{seminar_datum}}', label: 'Datum' },
    { key: '{{seminar_uhrzeit}}', label: 'Uhrzeit' },
    { key: '{{seminar_ort}}', label: 'Ort' },
    { key: '{{seminar_preis}}', label: 'Preis' },
    { key: '{{seminar_ue}}', label: 'UE' },
    { key: '{{kursnummer}}', label: 'Kursnummer' },
    { key: '{{referent}}', label: 'Referent' },
    { key: '{{veranstalter}}', label: 'Veranstalter' },
    { key: '{{portal_url}}', label: 'Portal-URL (nur URL-Text)' },
    { key: '{{bescheinigung_link}}', label: 'Bescheinigungs-URL (nur URL-Text)' },
    { key: '{{umfrage_link}}', label: 'Umfrage-URL (nur URL-Text)' },
    { key: '{{umfrage_block_html}}', label: 'Umfrage-Block (kompletter HTML-Block)' },
];

// Allow-Liste ALLER bekannten Platzhalter (Editor-Palette + kontext-spezifische,
// die erst beim Versand ersetzt werden — z. B. Zahlungs-/Lizenz-Mails). Wird vom
// Validator unten genutzt, um „verwaiste" Platzhalter zu warnen.
const KNOWN_PLACEHOLDER_KEYS = new Set([
    // Basis (aus _build_variables)
    'teilnehmer_vorname', 'teilnehmer_nachname', 'teilnehmer_name', 'teilnehmer_email',
    'teilnehmer_anrede',
    'seminar_titel', 'seminar_datum', 'seminar_uhrzeit', 'seminar_ort',
    'seminar_preis', 'seminar_ue', 'kursnummer', 'referent', 'veranstalter',
    'portal_url', 'bescheinigung_link', 'live_url', 'admin_url',
    'umfrage_link', 'umfrage_block_html',
    // Zahlungs-Mails (Rechnung / Beleg)
    'betrag', 'zahlungsdatum', 'zahlungsreferenz', 'rechnungsnummer',
    'zahlungsziel', 'zahlungsfrist', 'faelligkeitsdatum',
    'kontoinhaber', 'bank', 'iban', 'bic', 'verwendungszweck',
    // Online-Termine / Meeting
    'meeting_links_html', 'meeting_links_plain',
    // Warteliste
    'warteliste_annehmen_link', 'warteliste_ablehnen_link', 'warteliste_frist',
    // Lizenzverlängerung / Antrag
    'antragsdatum', 'eingereichte_ue', 'erforderliche_ue', 'erforderliche_ue_label',
    'erste_hilfe_status', 'ue_status_hinweis', 'ue_warnung_block',
    'lizenznummer', 'lizenztyp', 'gueltig_bis', 'pruefbedarf_anzahl',
]);

// Extrahiert alle {{...}}-Tokens (auch HTML-encoded), liefert Set unbekannter keys
export function findUnknownPlaceholders(...texts) {
    const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    const unknown = new Set();
    for (const t of texts) {
        if (!t) continue;
        let m;
        while ((m = re.exec(t)) !== null) {
            if (!KNOWN_PLACEHOLDER_KEYS.has(m[1])) unknown.add(m[1]);
        }
    }
    return Array.from(unknown);
}

/**
 * Visuelles Warn-Banner für Vorlagen-Editoren. Zeigt eine Liste der Platzhalter,
 * die im Text vorkommen, aber im Backend nicht (mehr) gerendert werden.
 * Nimmt beliebig viele Strings entgegen (z. B. subject + body_html).
 */
export function PlaceholderValidator({ texts = [] }) {
    const unknown = findUnknownPlaceholders(...texts);
    if (unknown.length === 0) return null;
    return (
        <div
            className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm"
            role="alert"
            data-testid="placeholder-validator-warning"
        >
            <span className="mt-0.5 text-amber-600">⚠</span>
            <div className="flex-1">
                <div className="font-semibold text-amber-900">
                    Unbekannte Platzhalter erkannt
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                    Folgende Platzhalter werden vom Backend nicht ersetzt und erscheinen so im Mail-Text wie geschrieben:
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {unknown.map((k) => (
                        <code
                            key={k}
                            className="text-[12px] font-mono bg-white border border-amber-300 text-amber-900 rounded px-1.5 py-0.5"
                        >
                            {`{{${k}}}`}
                        </code>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Vorkonfektionierte HTML-Link/Button-Snippets. Einfügen = Admin bekommt sofort
// einen funktionierenden Link zurück, falls er versehentlich gelöscht wurde.
const LINK_SNIPPETS = [
    {
        key: 'cta-portal',
        label: 'CTA-Button: Zum BGPR-Akademie-Portal',
        hint: 'Teal Button · {{portal_url}}',
        html: (
            `<p style="text-align:center;margin:24px 0;">` +
            `<a href="{{portal_url}}" style="display:inline-block;padding:12px 28px;` +
            `background:#0092B5;color:#ffffff;text-decoration:none;border-radius:6px;` +
            `font-weight:600;">Zum BGPR-Akademie-Portal</a></p>`
        ),
    },
    {
        key: 'cta-bescheinigung',
        label: 'CTA-Button: Bescheinigung herunterladen',
        hint: 'Teal Button · {{bescheinigung_link}}',
        html: (
            `<p style="text-align:center;margin:24px 0;">` +
            `<a href="{{bescheinigung_link}}" style="display:inline-block;padding:12px 28px;` +
            `background:#0092B5;color:#ffffff;text-decoration:none;border-radius:6px;` +
            `font-weight:600;">Bescheinigung herunterladen</a></p>`
        ),
    },
    {
        key: 'cta-umfrage',
        label: 'CTA-Button: An Umfrage teilnehmen',
        hint: 'Teal Button · {{umfrage_link}}',
        html: (
            `<p style="text-align:center;margin:24px 0;">` +
            `<a href="{{umfrage_link}}" style="display:inline-block;padding:10px 22px;` +
            `background:#0092B5;color:#ffffff;text-decoration:none;border-radius:6px;` +
            `font-weight:600;">Umfrage starten</a></p>`
        ),
    },
    {
        key: 'link-portal',
        label: 'Text-Link: Zur BGPR-Akademie',
        hint: 'Inline · {{portal_url}}',
        html: `<a href="{{portal_url}}">Zur BGPR-Akademie</a>`,
    },
    {
        key: 'link-bescheinigung',
        label: 'Text-Link: Bescheinigung im Portal öffnen',
        hint: 'Inline · {{bescheinigung_link}}',
        html: `<a href="{{bescheinigung_link}}">Bescheinigung im Portal öffnen</a>`,
    },
    {
        key: 'block-umfrage',
        label: 'Umfrage-Block (Intro + Button – automatisch befüllt)',
        hint: '{{umfrage_block_html}}',
        html: `{{umfrage_block_html}}`,
    },
];

function ToolbarButton({ onClick, active, disabled, children, title }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={`p-1.5 rounded transition-colors ${
                active
                    ? 'bg-gray-200 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            } ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            {children}
        </button>
    );
}

function ToolbarSeparator() {
    return <div className="w-px h-6 bg-gray-200 mx-1" />;
}

export function EmailEditor({ content, onChange, showPlaceholders = true }) {
    const [previewMode, setPreviewMode] = useState(false);
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                // StarterKit enthält bereits Link und Underline → hier deaktivieren, um
                // Duplicate-Extension-Warnings zu vermeiden.
                link: false,
                underline: false,
            }),
            Underline,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Link.configure({ openOnClick: false }),
            Image,
            Table.configure({ resizable: true }),
            TableRow,
            TableCell,
            TableHeader,
        ],
        content: content || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    // Externe Content-Änderungen (z. B. beim Laden einer Vorlage) in den TipTap-Editor übernehmen.
    // TipTap initialisiert `content` nur einmal beim Mount, daher müssen wir nachträgliche
    // Wechsel manuell synchronisieren – aber NUR wenn der Inhalt wirklich abweicht, sonst
    // gerät jeder Tastendruck in eine Endlosschleife (onUpdate → parent → useEffect → setContent).
    useEffect(() => {
        if (!editor) return;
        const incoming = content || '';
        const current = editor.getHTML();
        // TipTap fügt im leeren Zustand "<p></p>" ein – diesen Edge-Case ignorieren
        if (incoming === current) return;
        if (!incoming && (current === '<p></p>' || current === '')) return;
        editor.commands.setContent(incoming, false);
    }, [content, editor]);

    const setLink = useCallback(() => {
        if (!editor) return;
        const prev = editor.getAttributes('link').href;
        const url = window.prompt('URL eingeben:', prev);
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }
    }, [editor]);

    const addImage = useCallback(() => {
        if (!editor) return;
        const url = window.prompt('Bild-URL eingeben:');
        if (url) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    }, [editor]);

    const insertPlaceholder = useCallback((placeholder) => {
        if (!editor) return;
        editor.chain().focus().insertContent(placeholder).run();
    }, [editor]);

    const insertHtml = useCallback((html) => {
        if (!editor) return;
        editor.chain().focus().insertContent(html).run();
    }, [editor]);

    const setColor = useCallback((color) => {
        if (!editor) return;
        editor.chain().focus().setColor(color).run();
    }, [editor]);

    if (!editor) return null;

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="email-editor">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
                <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Rückgängig">
                    <Undo className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Wiederholen">
                    <Redo className="h-4 w-4" />
                </ToolbarButton>

                <ToolbarSeparator />

                <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Überschrift 1">
                    <Heading1 className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Überschrift 2">
                    <Heading2 className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Überschrift 3">
                    <Heading3 className="h-4 w-4" />
                </ToolbarButton>

                <ToolbarSeparator />

                <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Fett">
                    <Bold className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Kursiv">
                    <Italic className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Unterstrichen">
                    <UnderlineIcon className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Durchgestrichen">
                    <Strikethrough className="h-4 w-4" />
                </ToolbarButton>

                <ToolbarSeparator />

                {/* Color Picker */}
                <div className="relative">
                    <input
                        type="color"
                        onChange={(e) => setColor(e.target.value)}
                        className="absolute inset-0 opacity-0 w-8 h-8 cursor-pointer"
                        title="Textfarbe"
                    />
                    <div className="p-1.5 text-gray-600 hover:bg-gray-100 rounded cursor-pointer">
                        <Palette className="h-4 w-4" />
                    </div>
                </div>
                <ToolbarButton onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()} active={editor.isActive('highlight')} title="Markieren">
                    <Highlighter className="h-4 w-4" />
                </ToolbarButton>

                <ToolbarSeparator />

                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Links">
                    <AlignLeft className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Zentriert">
                    <AlignCenter className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Rechts">
                    <AlignRight className="h-4 w-4" />
                </ToolbarButton>

                <ToolbarSeparator />

                <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Aufzählung">
                    <List className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Nummerierung">
                    <ListOrdered className="h-4 w-4" />
                </ToolbarButton>

                <ToolbarSeparator />

                <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Link">
                    <LinkIcon className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={addImage} title="Bild">
                    <ImageIcon className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Tabelle">
                    <TableIcon className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Trennlinie">
                    <Minus className="h-4 w-4" />
                </ToolbarButton>

                <ToolbarSeparator />

                {/* Links-Snippets Dropdown – füllt versehentlich gelöschte CTA-Links/Buttons schnell wieder ein */}
                <div className="relative group">
                    <button
                        type="button"
                        className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded transition-colors"
                        title="Vorkonfektionierte Links & CTA-Buttons einfügen"
                        data-testid="editor-links-trigger"
                    >
                        <Link2 className="h-3.5 w-3.5" />
                        Links
                    </button>
                    <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 max-h-80 overflow-y-auto">
                        <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                            Klick fügt fertigen HTML-Link ein
                        </div>
                        {LINK_SNIPPETS.map((s) => (
                            <button
                                key={s.key}
                                type="button"
                                onClick={() => insertHtml(s.html)}
                                className="w-full text-left px-3 py-2 hover:bg-gray-50 group/item"
                                data-testid={`editor-link-snippet-${s.key}`}
                            >
                                <div className="text-sm text-gray-800 group-hover/item:text-gray-900">{s.label}</div>
                                <code className="text-[11px] text-gray-400">{s.hint}</code>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Placeholder Dropdown */}
                {showPlaceholders && (
                <div className="relative group">
                    <button
                        type="button"
                        className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                        title="Platzhalter einfügen"
                    >
                        <Braces className="h-3.5 w-3.5" />
                        Platzhalter
                    </button>
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 max-h-80 overflow-y-auto">
                        {PLACEHOLDERS.map((p) => (
                            <button
                                key={p.key}
                                type="button"
                                onClick={() => insertPlaceholder(p.key)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex justify-between items-center"
                            >
                                <span className="text-gray-700">{p.label}</span>
                                <code className="text-xs text-gray-400">{p.key}</code>
                            </button>
                        ))}
                    </div>
                </div>
                )}

                {/* Preview Toggle – rechts außen */}
                <div className="ml-auto">
                    <button
                        type="button"
                        onClick={() => setPreviewMode(v => !v)}
                        className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded transition-colors ${previewMode ? 'bg-gray-900 text-white hover:bg-gray-800' : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-100'}`}
                        title={previewMode ? 'Bearbeiten' : 'Vorschau anzeigen'}
                        data-testid="editor-preview-toggle"
                    >
                        {previewMode ? <><Pencil className="h-3.5 w-3.5" /> Bearbeiten</> : <><Eye className="h-3.5 w-3.5" /> Vorschau</>}
                    </button>
                </div>
            </div>

            {/* Editor Content / Preview – WYSIWYG-Rahmen wie im echten E-Mail-Client */}
            {previewMode ? (
                <EmailWysiwygPreview html={editor.getHTML()} />
            ) : (
                <div className="bg-gray-100 p-4 md:p-6">
                    <div className="mx-auto max-w-[640px] bg-white rounded-md shadow-sm border border-gray-200">
                        <EditorContent
                            editor={editor}
                            className="prose prose-sm max-w-none px-6 py-6 min-h-[340px] focus:outline-none text-[15px] leading-[1.65] text-gray-800 [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[340px] [&_.ProseMirror]:font-[system-ui,Arial,Helvetica,sans-serif] [&_.ProseMirror_a]:text-[#0092B5] [&_.ProseMirror_a]:underline [&_.ProseMirror_p]:my-3 [&_.ProseMirror_h1]:text-[22px] [&_.ProseMirror_h2]:text-[19px] [&_.ProseMirror_h3]:text-[17px] [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6 [&_.ProseMirror_ul]:my-3 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_ol]:my-3 [&_.ProseMirror_li]:my-1 [&_.ProseMirror_table]:border-collapse [&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-gray-300 [&_.ProseMirror_td]:p-2 [&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-gray-300 [&_.ProseMirror_th]:p-2 [&_.ProseMirror_th]:bg-gray-50 [&_.ProseMirror_th]:font-semibold"
                        />
                    </div>
                    <p className="text-[11px] text-gray-400 text-center mt-3 italic">
                        Vorschau-Rahmen – tatsächliche Darstellung beim Empfänger kann je nach Mail-Client leicht abweichen.
                    </p>
                </div>
            )}
        </div>
    );
}

// Zeigt das fertige HTML inkl. Platzhalter-Highlighting in einer iframe-ähnlichen Mail-Kachel.
function EmailWysiwygPreview({ html }) {
    // Platzhalter optisch erkennbar machen, OHNE in HTML-Attribute (href, style …)
    // hineinzubrechen. Daher: Markup parsen und nur Text-Nodes austauschen.
    const highlighted = React.useMemo(() => {
        const safe = html || '';
        if (!safe) return '';
        try {
            const doc = new DOMParser().parseFromString(`<div>${safe}</div>`, 'text/html');
            const root = doc.body.firstChild;
            const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
            const targets = [];
            let node;
            while ((node = walker.nextNode())) {
                if (/{{\s*[\w.]+\s*}}/.test(node.textContent)) targets.push(node);
            }
            targets.forEach(textNode => {
                const parent = textNode.parentNode;
                const parts = textNode.textContent.split(/({{\s*[\w.]+\s*}})/g);
                const frag = doc.createDocumentFragment();
                parts.forEach(part => {
                    if (/^{{\s*[\w.]+\s*}}$/.test(part)) {
                        const span = doc.createElement('span');
                        span.setAttribute('style', 'background:#fef3c7;color:#92400e;padding:1px 4px;border-radius:3px;font-family:monospace;font-size:0.85em;');
                        span.textContent = part;
                        frag.appendChild(span);
                    } else {
                        frag.appendChild(doc.createTextNode(part));
                    }
                });
                parent.replaceChild(frag, textNode);
            });
            return root.innerHTML;
        } catch {
            return safe;
        }
    }, [html]);
    return (
        <div className="bg-gray-100 p-4 md:p-6" data-testid="editor-preview">
            <div
                className="prose prose-sm max-w-none mx-auto bg-white rounded-md shadow-sm border border-gray-200 px-6 py-6 min-h-[340px] text-[15px] leading-[1.65] text-gray-800 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
                style={{ fontFamily: 'system-ui, Arial, Helvetica, sans-serif', maxWidth: '640px' }}
                dangerouslySetInnerHTML={{ __html: highlighted || '<p style="color:#9ca3af;text-align:center;padding:40px 0;">(Leere Mail)</p>' }}
            />
            <p className="text-[11px] text-gray-500 text-center mt-3">
                <span className="inline-block px-1.5 bg-yellow-100 text-yellow-800 rounded font-mono mr-1">{'{{ … }}'}</span>
                = Platzhalter, wird beim Versand dynamisch ersetzt.
            </p>
        </div>
    );
}
